import React, { useEffect, useState, useCallback } from "react";
import { Link } from 'react-router-dom';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import axios from "axios";
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import { PulseLoader } from "react-spinners";
import PurchaseOrderTable from "../../components/purchaseOrders/PurchaseOrderTable";
import PermissionGate from "../../components/common/PermissionGate";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import ReportDownloader from "../../components/common/ReportDownloader";

export default function PurchaseOrderList() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Permission checks
  const canViewPO = useHasPermission(PERMISSIONS.PO_VIEW);
  const canCreatePO = useHasPermission(PERMISSIONS.PO_CREATE);
  const canEditPO = useHasPermission(PERMISSIONS.PO_EDIT);
  const canDeletePO = useHasPermission(PERMISSIONS.PO_DELETE);

  // useEffect(() => {
  //   if (location.state?.toast) {
  //     toast.success(location.state.toast);
  //   }
  // }, [location]);

  // Fetch purchase orders
  const fetchOrders = useCallback(async () => {
    if (!canViewPO) {
      toast.error("You don't have permission to view purchase orders");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/purchase-orders?populate=true&populateItems=true`);
      setOrders(res.data);
      if (sessionStorage.getItem('requestSuccess') === 'true') {
        toast.success('Request added successfully!');
        sessionStorage.removeItem('requestSuccess');
      }
      if (sessionStorage.getItem('pocreationSuccess') === 'true') {
        toast.success('Purchase Order created successfully');
        sessionStorage.removeItem('pocreationSuccess');
      }
    } catch (err) {
      console.error("Error fetching purchase orders", err);
      toast.error("Failed to fetch purchase orders");
    } finally {
      setIsLoading(false);
    } 
  }, [canViewPO]);

  useEffect(() => {
    fetchOrders();
  }, [canViewPO, fetchOrders]);

  // If user can't view PO, show permission denied
  if (!canViewPO) {
    return (
      <Layout title="Purchase Orders">
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view purchase orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout title="Purchase Orders">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Purchase Orders</li>
        </ol>
      </nav>

      {/* Purchase Orders Header */}
      <Row className="mb-4 gy-3">
        <Col className="col">
          <h2>Purchase Orders</h2>
          <small className="text-muted">
            {canEditPO ? "View and manage" : "View"} purchase orders
            {!canCreatePO && !canEditPO && !canDeletePO && " (read-only)"}
          </small>
        </Col>
        <Col className="text-end col-auto">
          <div className="d-flex justify-content-end flex-wrap gap-2">
            <PermissionGate require={PERMISSIONS.QUOTATION_VIEW}>
              <Link className="btn btn-secondary" to='/approved-quotations'>
                <i className="fas fa-check-circle me-2" /> View Approved Quotations
              </Link>
            </PermissionGate>

            <PermissionGate
              require={PERMISSIONS.PO_CREATE}
              fallback={
                <Button variant="outline-primary" disabled title="You don't have permission to create purchase orders">
                  <i className="fas fa-plus me-2" /> New Purchase Order
                </Button>
              }
            >
              <Link to="/new-purchase-order" className="btn btn-primary">
                <i className="fas fa-plus me-2" /> New Purchase Order
              </Link>
            </PermissionGate>

            <PermissionGate require={PERMISSIONS.PO_VIEW}>
              <Link className="btn btn-outline-info" to='/packaging-material-purchase-orders'>
                <i className="fas fa-box me-2" /> Packing Material POs
              </Link>
            </PermissionGate>
            
            {/* <Button variant="outline-primary" onClick={() => setShowExportModal(true)}>
              <FiDownload className="me-2" /> Download Report
            </Button> */}
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-4">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      ) : (
        <>
          <PurchaseOrderTable
            orders={orders}
            canEdit={canEditPO}
            canDelete={canDeletePO}
            canView={canViewPO}
          />

          <ReportDownloader 
            show={showExportModal}
            handleClose={() => setShowExportModal(false)}
            data={orders}
            availableColumns={[
              { header: 'SN', key: 'sn' },
              { header: 'PO Number', key: 'orderId' },
              { header: 'Order Date', key: 'orderDate' },
              { header: 'Vendor Name', key: 'vendorId.business_name' },
              { header: 'Broker Name', key: 'brokerId.business_name' },
              { header: 'Product', key: 'items.productId.productName' },
              { header: 'Quality', key: 'items.quality' },
              { header: 'Ordered Quantity', key: 'items.orderedQuantity' },
              { header: 'Unit Price', key: 'items.rate' },
              { header: 'Total Item Amount', key: 'items.totalPrice' },
              { header: 'Quality Check Qty', key: 'items.qualityCheckedQuantity' },
              { header: 'Unloaded Qty', key: 'items.unloadedQuantity' },
              { header: 'Returned Qty', key: 'items.returnedQuantity' },
              { header: 'Return Details', key: 'items.returnDetails' },
              { header: 'EDD', key: 'expectedDeliveryDate' },
              { header: 'Status', key: 'status' },
              { header: 'Items SubTotal', key: 'itemsTotalPrice' },
              { header: 'Tax (%)', key: 'tax' },
              { header: 'Grand Total', key: 'finalPrice' },
              { header: 'Payment Status', key: 'paymentStatus' }
            ]}
            title="Export Purchase Orders"
            fileName="purchase_orders"
          />
        </>
      )}
    </Layout>
  );
}
